import { COUNTRY_CONFIG, type Region } from "../config/countries";
import type { ResolvedProvider } from "../config/energyProvider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { formatCoefficient, formatEuro } from "@/utils/format";
import { providerDisplayName } from "@/utils/providerConditions";
import { STRINGS } from "@/i18n";

interface HeaderControlsProps {
  providers: ResolvedProvider[];
  selectedProvider: string;
  onProviderChange: (providerId: string) => void;
  includeTaxes: boolean;
  regions: Region[] | null;
  regionId?: string | null;
  onRegionChange?: (regionId: string) => void;
}

export function HeaderControls({
  providers,
  selectedProvider,
  onProviderChange,
  includeTaxes,
  regions,
  regionId,
  onRegionChange,
}: HeaderControlsProps) {
  const selectedProviderData = providers.find(
    (provider) => provider.id === selectedProvider
  );
  const providerMarkup = selectedProviderData
    ? selectedProviderData.perKwhChargeExcludingVat *
      (includeTaxes ? 1 + COUNTRY_CONFIG.vatRate : 1)
    : null;

  return (
    <div className="border-t border-border">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:flex-wrap lg:items-center lg:gap-5 lg:px-8 xl:flex-nowrap">
        {regions && onRegionChange && (
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Label
              htmlFor="header-region-select"
              className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
            >
              {STRINGS.controls.region}
            </Label>
            <Select
              value={regionId ?? undefined}
              onValueChange={onRegionChange}
            >
              <SelectTrigger
                id="header-region-select"
                className="h-10 w-full bg-card sm:w-48"
              >
                <SelectValue placeholder={STRINGS.controls.regionPlaceholder} />
              </SelectTrigger>
              <SelectContent className="w-48">
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div
          className={cn(
            "contents",
            regions &&
              onRegionChange &&
              "lg:ml-auto lg:flex lg:flex-wrap lg:items-center lg:gap-5 xl:flex-nowrap"
          )}
        >
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Label
              htmlFor="header-provider-select"
              className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
            >
              {STRINGS.controls.provider}
            </Label>
            <Select value={selectedProvider} onValueChange={onProviderChange}>
              <SelectTrigger
                id="header-provider-select"
                className="h-10 w-full bg-card sm:w-88"
              >
                <SelectValue placeholder={STRINGS.controls.providerPlaceholder} />
              </SelectTrigger>
              <SelectContent className="w-88 max-w-[calc(100vw-2rem)]">
                {providers.map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {providerDisplayName(provider)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProviderData && providerMarkup !== null && (
            <div className="flex w-fit max-w-full shrink-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted px-4 py-2 text-xs text-muted-foreground xl:flex-nowrap xl:whitespace-nowrap">
              {selectedProviderData.spotCoefficient &&
                selectedProviderData.spotCoefficient !== 1 && (
                  <>
                    <span>
                      {STRINGS.controls.factor}{" "}
                      <strong className="font-semibold text-foreground">
                        ×{formatCoefficient(selectedProviderData.spotCoefficient)}
                      </strong>
                    </span>
                    <span className="h-4 w-px bg-border" aria-hidden="true" />
                  </>
                )}
              <span>
                {STRINGS.controls.markup}{" "}
                <strong className="font-semibold text-foreground">
                  {formatEuro(providerMarkup, 3)}
                </strong>
                /kWh
              </span>
              <span className="h-4 w-px bg-border" aria-hidden="true" />
              <span>
                {STRINGS.controls.monthlyFee}{" "}
                <strong className="font-semibold text-foreground">
                  {formatEuro(selectedProviderData.monthlyFee, 2)}
                </strong>
                {STRINGS.controls.perMonth}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface IntervalToggleProps {
  interval: "60m" | "15m";
  onIntervalChange: (interval: "60m" | "15m") => void;
}

export function IntervalToggle({
  interval,
  onIntervalChange,
}: IntervalToggleProps) {
  return (
    <div className="inline-flex items-center">
      <div
        role="group"
        aria-label={STRINGS.controls.intervalGroup}
        className="inline-flex items-center gap-1"
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={interval === "60m"}
          onClick={() => onIntervalChange("60m")}
          className={cn(
            "relative h-10 min-w-16 rounded-none px-3 text-sm after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:content-[''] lg:h-8 lg:min-w-12 lg:text-xs",
            interval === "60m"
              ? "font-semibold text-primary after:bg-primary hover:bg-transparent hover:text-primary"
              : "text-muted-foreground after:bg-transparent hover:bg-transparent hover:text-foreground hover:after:bg-border"
          )}
        >
          {STRINGS.controls.hour}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-pressed={interval === "15m"}
          onClick={() => onIntervalChange("15m")}
          className={cn(
            "relative h-10 min-w-16 rounded-none px-3 text-sm after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full after:content-[''] lg:h-8 lg:min-w-12 lg:text-xs",
            interval === "15m"
              ? "font-semibold text-primary after:bg-primary hover:bg-transparent hover:text-primary"
              : "text-muted-foreground after:bg-transparent hover:bg-transparent hover:text-foreground hover:after:bg-border"
          )}
        >
          {STRINGS.controls.quarter}
        </Button>
      </div>
    </div>
  );
}

interface TaxToggleProps {
  id: string;
  includeTaxes: boolean;
  onTaxesChange: (include: boolean) => void;
}

export function TaxToggle({
  id,
  includeTaxes,
  onTaxesChange,
}: TaxToggleProps) {
  return (
    <div className="inline-flex h-9 items-center gap-2">
      <Label
        htmlFor={id}
        className="inline-grid cursor-pointer whitespace-nowrap text-center text-xs font-normal text-foreground"
      >
        <span
          aria-hidden={!includeTaxes}
          className={cn("[grid-area:1/1]", !includeTaxes && "invisible")}
        >
          {STRINGS.controls.inclTaxes}
        </span>
        <span
          aria-hidden={includeTaxes}
          className={cn("[grid-area:1/1]", includeTaxes && "invisible")}
        >
          {STRINGS.controls.exclTaxes}
        </span>
      </Label>
      <Switch
        id={id}
        aria-label={STRINGS.controls.taxesToggle}
        className="border-muted-foreground/40"
        checked={includeTaxes}
        onCheckedChange={onTaxesChange}
      />
    </div>
  );
}
